/**
 * SectionQuestions
 *
 * V2-specific component that manages and renders the ordered list of questions
 * within a single survey section. Wraps QuestionField (the V1 presentational
 * component) via a per-section useFieldArray so that V1's FieldList — which is
 * tightly coupled to the flat `customForm.fields` path — is never touched.
 *
 * Advanced features (per-question conditional visibility, tag prefix, custom-field
 * mapping) are intentionally omitted and deferred to follow-on work.
 */
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '@Models/index';
import {
	FormFieldType,
	ICustomFieldMap,
	ICustomFormFieldTagSettings,
	IFormField,
	ITagGenerationSettings,
} from '@ViewModels';
import { inject } from 'mobx-react';
import { css } from 'aphrodite';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { ISelectBoxOption } from '@WebComponents/SelectBox';
import { useUserSession } from '../../../../../../../../models/hooks/appStateHooks';
import { useContactCustomFields } from '../../../../../../../../queries/Contact/useContactCustomFields';
import { QuestionField } from '../../../AdditionalQuestions/components/QuestionField';
import type { CustomFieldManagerProps, QuestionFieldProps, TagManagerProps } from '../../../AdditionalQuestions/types';
import type { SurveyPart } from '../../types';
import { applyVisibilityCleanup } from '../../utils/validation';
import { isQuestionMappingDirty } from '../../utils/questionMappingDirty';
import { QuestionVisibilityControls } from './QuestionVisibilityControls';
import { styleSheet as questionStyleSheet } from './SectionQuestions.styles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SectionQuestionsProps {
	/** RHF path prefix for this section, e.g. "surveyForm.surveyParts.0" */
	fieldNamePrefix: string;
	/** Zero-based index of this section inside surveyParts. */
	sectionIndex: number;
	/** Whether the parent survey is in edit (vs create) mode. */
	isEdit: boolean;
	/** Full ordered list of survey parts — used by per-question visibility to enumerate eligible source questions. */
	allSurveyParts: SurveyPart[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map V1 FormFieldType enum to the V2 SurveyQuestion `_type` discriminator.
 * V2 questions need both: `fieldType` for legacy renderers, `_type` for visibility
 * eligibility filters (SectionTriggers, QuestionVisibilityControls) and BE shape.
 */
export function fieldTypeToQuestionType(
	fieldType: FormFieldType
): 'ShortAnswerQuestion' | 'DropDownQuestion' | 'CheckBoxesQuestion' | 'FileUploadQuestion' {
	switch (fieldType) {
		case FormFieldType.Option:
			return 'DropDownQuestion';
		case FormFieldType.MultipleOptions:
			return 'CheckBoxesQuestion';
		case FormFieldType.FileUpload:
			return 'FileUploadQuestion';
		default:
			return 'ShortAnswerQuestion';
	}
}

/**
 * Drill into a nested dirtyFields object and return true if any leaf is `true`.
 * react-hook-form marks dirty leaves as `true`; untouched values are absent.
 */
function hasDirtyLeaf(node: unknown): boolean {
	if (node === true) return true;
	if (node && typeof node === 'object') {
		return Object.values(node).some(hasDirtyLeaf);
	}
	return false;
}

/**
 * Return true if any leaf of an RHF `errors` subtree is an error object
 * (a node with a string `message` property). RHF error leaves carry `message`,
 * unlike dirtyFields whose leaves are bare `true`.
 */
function hasErrorLeaf(node: unknown): boolean {
	if (!node || typeof node !== 'object') return false;
	if (typeof (node as { message?: unknown }).message === 'string') return true;
	return Object.values(node).some(hasErrorLeaf);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders all questions belonging to one survey section, providing add / remove /
 * update callbacks that delegate into react-hook-form's field-array API.
 *
 * Prop-required callbacks forwarded to each QuestionField:
 *  - onFieldUpdate     — full field replacement via setValue
 *  - onFieldRemove     — removes entry from the field array
 *  - onLabelChange     — updates label in-place via setValue
 *  - onToggleOptional  — toggles isRequired via setValue
 *  - onFieldTypeChange — changes fieldType via setValue
 */
const SectionQuestionsBase: React.FC<SectionQuestionsProps & IImpersonationContextComponentProps> = ({
	fieldNamePrefix,
	sectionIndex,
	isEdit,
	allSurveyParts,
	impersonationContext,
}) => {
	const { control, setValue, formState, getValues } = useFormContext();
	const questionsPath = `${fieldNamePrefix}.questions` as const;

	// V2 reads/writes tag-generation settings at the SAME root paths V1 uses —
	// `tagGenerationSettings` and `customFieldMap` live on the CustomSurvey
	// form regardless of which editor (V1 or V2) is mounted, so tag prefixes
	// and custom-field mappings keyed by `fieldId` interoperate across editors.
	const tagGenerationSettings = useWatch({ control, name: 'tagGenerationSettings' }) as
		| Partial<ITagGenerationSettings>
		| undefined;
	const customFieldMap = useWatch({ control, name: 'customFieldMap' }) as ICustomFieldMap[] | undefined;

	// Available contact custom fields drive the "Link Answer to Custom Field" picker.
	// Mirrors V1 `AdditionalQuestions/index.tsx:252`. Gated on the account feature flag
	// (`hasCustomContactFields`) — query is a no-op otherwise.
	const userSession = useUserSession();
	const hasCustomContactFields = !!userSession?.account?.additionalInfo?.hasCustomContactFields;
	const customFieldsQuery = useContactCustomFields({
		impersonationContext,
		refetchOnWindowFocus: false,
		enabled: hasCustomContactFields,
	});
	const availableCustomFields = customFieldsQuery.data?.fields ?? [];

	// Contact-field IDs already claimed by SOME question; used to disable those
	// rows in another question's picker so one contact field maps to at most
	// one question. Each question filters out its OWN mapping before consuming
	// this list (so the user can re-pick their existing target on edit).
	const usedCustomFieldIds = React.useMemo(() => (customFieldMap ?? []).map(m => m.contactFieldId), [customFieldMap]);

	// Use useFieldArray for structural ops (append, remove). Render from `useWatch`
	// so in-place sub-path setValue calls (label, type, isOptional) refresh the rendered
	// inputs without forcing the QuestionField to unmount (which would steal focus).
	const { remove } = useFieldArray({
		control,
		name: questionsPath,
		keyName: '_rhfId',
	});
	const watchedQuestions = (useWatch({ control, name: questionsPath }) ?? []) as IFormField<string>[];

	// Question IDs currently persisted to the BE. Derived from
	// `formState.defaultValues` so it tracks the persisted state across the
	// survey lifecycle: the post-load `reset(getValues())` in CustomSurvey
	// (~1000ms after load) AND the mutation `onSuccess` reset both update
	// `defaultValues`, so newly-saved questions land in this set automatically
	// and their type-selector flips to disabled. Gated on `isEdit`: in create
	// mode `defaultValues` still holds the seed `createBlankQuestion()` UUID
	// which must NOT count as saved.
	const savedQuestionIds = React.useMemo(() => {
		if (!isEdit) return new Set<string>();
		const parts = (
			formState.defaultValues as
				| { surveyForm?: { surveyParts?: Array<{ questions?: Array<{ id?: string }> }> } }
				| undefined
		)?.surveyForm?.surveyParts;
		if (!parts) return new Set<string>();
		const ids = parts
			.flatMap(p => p?.questions ?? [])
			.map(q => q?.id)
			.filter((id): id is string => !!id);
		return new Set(ids);
	}, [isEdit, formState.defaultValues]);

	// ---------------------------------------------------------------------------
	// Derived state per field (dirty / saved / error)
	// ---------------------------------------------------------------------------

	const { dirtyFields, errors } = formState;

	// Build supported field type options — same shape as V1's formFieldOptions.
	// V2 sections are always within a Custom survey context (the only survey type
	// that supports sections), so we mirror V1's custom-survey supportedFieldTypes.
	// QuestionFieldProps['formFieldOptions'] expects `{ title: string; value: FormFieldType }[]`.
	const formFieldOptions: QuestionFieldProps['formFieldOptions'] = [
		{ title: 'Short Answer', value: FormFieldType.String },
		{ title: 'Dropdown', value: FormFieldType.Option },
		{ title: 'Checkboxes', value: FormFieldType.MultipleOptions },
		{ title: 'File Upload', value: FormFieldType.FileUpload },
	];

	// ---------------------------------------------------------------------------
	// Callbacks
	// ---------------------------------------------------------------------------

	/**
	 * QuestionFieldProps.onFieldUpdate signature: (field, index) — full field replacement.
	 * Use setValue at the sub-path so the existing field-array entry's `_rhfId` is
	 * preserved (avoiding remount + focus loss).
	 */
	const onFieldUpdate = React.useCallback(
		(updatedField: IFormField<string>, questionIndex: number) => {
			setValue(`${questionsPath}.${questionIndex}`, updatedField, { shouldDirty: true });
			// Field replacement covers option add/remove/label edits — sweep so any
			// visibility rule pointing at a no-longer-existing or now-blank option
			// is dropped immediately (mirrors V1 `cleanupConditionalVisibility`).
			applyVisibilityCleanup(getValues, setValue);
		},
		[setValue, getValues, questionsPath]
	);

	/**
	 * QuestionFieldProps.onFieldRemove signature: (index) — structural change, use remove.
	 */
	const onFieldRemove = React.useCallback(
		(questionIndex: number) => {
			remove(questionIndex);
			// Removing a question can invalidate visibility rules elsewhere that
			// referenced it as a trigger source. Sweep after the structural change.
			applyVisibilityCleanup(getValues, setValue);
		},
		[remove, getValues, setValue]
	);

	/**
	 * QuestionFieldProps.onLabelChange signature: (e, index).
	 * In-place edit via sub-path setValue — preserves the field-array entry's identity
	 * so the controlled input doesn't lose focus mid-typing.
	 */
	const onLabelChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>, questionIndex: number) => {
			setValue(`${questionsPath}.${questionIndex}.label`, e.target.value, { shouldDirty: true });
		},
		[setValue, questionsPath]
	);

	/**
	 * QuestionFieldProps.onToggleOptional signature: (index) — flips the V1 `isOptional`
	 * field (NOT `isRequired` — V1's IFormField uses isOptional).
	 */
	const onToggleOptional = React.useCallback(
		(questionIndex: number) => {
			const current = getValues(`${questionsPath}.${questionIndex}.isOptional`) as boolean | undefined;
			setValue(`${questionsPath}.${questionIndex}.isOptional`, !current, { shouldDirty: true });
		},
		[setValue, getValues, questionsPath]
	);

	/**
	 * QuestionFieldProps.onFieldTypeChange signature: (selectedType: ISelectBoxOption<FormFieldType>, index).
	 */
	const onFieldTypeChange = React.useCallback(
		(selectedType: ISelectBoxOption<FormFieldType>, questionIndex: number) => {
			const ft = selectedType.value as FormFieldType;
			setValue(`${questionsPath}.${questionIndex}.fieldType`, ft, { shouldDirty: true });
			// Keep the V2 `_type` discriminator in sync so visibility eligibility
			// filters (SectionTriggers, QuestionVisibilityControls) can see this
			// question when it becomes a Dropdown / Checkbox source.
			setValue(`${questionsPath}.${questionIndex}._type`, fieldTypeToQuestionType(ft), { shouldDirty: true });
			// If the question is no longer a Dropdown/Checkbox, drop any visibility
			// rule elsewhere that pointed at it as a trigger source.
			applyVisibilityCleanup(getValues, setValue);
			// Prune stale tag-prefix / custom-field mappings if the new type no
			// longer supports them. Mirrors V1 `handleFieldTypeChange` —
			// tag-prefix: only Option/MultipleOptions; custom-field: any of the
			// supported types (String/Option/MultipleOptions/FileUpload for Custom
			// Surveys, which is the only survey type that mounts V2).
			const fieldId = getValues(`${questionsPath}.${questionIndex}.id`) as string | undefined;
			if (!fieldId) return;
			if (ft !== FormFieldType.Option && ft !== FormFieldType.MultipleOptions) {
				const existing = tagGenerationSettings?.customFormFieldTagSettings;
				if (existing?.some((s: ICustomFormFieldTagSettings) => s.fieldId === fieldId)) {
					setValue(
						'tagGenerationSettings',
						{
							...tagGenerationSettings,
							customFormFieldTagSettings: existing.filter((s: ICustomFormFieldTagSettings) => s.fieldId !== fieldId),
						},
						{ shouldDirty: true }
					);
				}
			}
			const customFieldSupported =
				ft === FormFieldType.String ||
				ft === FormFieldType.Option ||
				ft === FormFieldType.MultipleOptions ||
				ft === FormFieldType.FileUpload;
			if (!customFieldSupported && customFieldMap?.some(m => m.fieldId === fieldId)) {
				setValue(
					'customFieldMap',
					customFieldMap.filter(m => m.fieldId !== fieldId),
					{ shouldDirty: true }
				);
			}
		},
		[setValue, getValues, questionsPath, tagGenerationSettings, customFieldMap]
	);

	/**
	 * Upsert a tag-prefix entry for `fieldId`. Mirrors V1's
	 * `AdditionalQuestions/index.tsx:handleTagPrefixChange` but writes via RHF's
	 * shouldDirty flag instead of V1's fieldStateManager. Wipes any custom-field
	 * mapping on the same fieldId to preserve V1's tag/custom-field mutual
	 * exclusivity — both editors read the same root-level state.
	 */
	const onTagPrefixChange = React.useCallback(
		(fieldId: string, tagPrefix: string) => {
			const current = tagGenerationSettings ?? {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [],
			};
			const existing = current.customFormFieldTagSettings ?? [];
			const idx = existing.findIndex((s: ICustomFormFieldTagSettings) => s.fieldId === fieldId);
			const nextEntries = [...existing];
			const entry = { fieldId, shouldGenerateTag: true, tagPrefix };
			if (idx >= 0) nextEntries[idx] = entry;
			else nextEntries.push(entry);
			setValue('tagGenerationSettings', { ...current, customFormFieldTagSettings: nextEntries }, { shouldDirty: true });
			if (customFieldMap?.some(m => m.fieldId === fieldId)) {
				setValue(
					'customFieldMap',
					customFieldMap.filter(m => m.fieldId !== fieldId),
					{ shouldDirty: true }
				);
			}
		},
		[tagGenerationSettings, customFieldMap, setValue]
	);

	/** Remove the tag-prefix entry for `fieldId`. Mirrors V1's `handleTagPrefixDelete`. */
	const onTagPrefixDelete = React.useCallback(
		(fieldId: string) => {
			if (!tagGenerationSettings?.customFormFieldTagSettings?.length) return;
			setValue(
				'tagGenerationSettings',
				{
					...tagGenerationSettings,
					customFormFieldTagSettings: tagGenerationSettings.customFormFieldTagSettings.filter(
						(s: ICustomFormFieldTagSettings) => s.fieldId !== fieldId
					),
				},
				{ shouldDirty: true }
			);
		},
		[tagGenerationSettings, setValue]
	);

	/**
	 * Upsert a custom-field mapping for `fieldId`. Mirrors V1's
	 * `handleCustomFieldChange` — the BE expects `contactFieldId` to hold the
	 * field NAME, not its id (see V1 comment at `index.tsx:786`). Wipes any
	 * tag-prefix for the same fieldId to preserve V1's mutual-exclusivity.
	 */
	const onCustomFieldChange = React.useCallback(
		(fieldId: string, _customFieldId: string, customFieldName: string) => {
			const current = customFieldMap ?? [];
			const idx = current.findIndex(m => m.fieldId === fieldId);
			const next = [...current];
			const entry: ICustomFieldMap = { fieldId, contactFieldId: customFieldName };
			if (idx >= 0) next[idx] = entry;
			else next.push(entry);
			setValue('customFieldMap', next, { shouldDirty: true });
			if (tagGenerationSettings?.customFormFieldTagSettings?.some(s => s.fieldId === fieldId)) {
				setValue(
					'tagGenerationSettings',
					{
						...tagGenerationSettings,
						customFormFieldTagSettings: tagGenerationSettings.customFormFieldTagSettings.filter(
							(s: ICustomFormFieldTagSettings) => s.fieldId !== fieldId
						),
					},
					{ shouldDirty: true }
				);
			}
		},
		[customFieldMap, tagGenerationSettings, setValue]
	);

	/** Remove the custom-field mapping for `fieldId`. Mirrors V1's `handleCustomFieldDelete`. */
	const onCustomFieldDelete = React.useCallback(
		(fieldId: string) => {
			if (!customFieldMap?.length) return;
			setValue(
				'customFieldMap',
				customFieldMap.filter(m => m.fieldId !== fieldId),
				{ shouldDirty: true }
			);
		},
		[customFieldMap, setValue]
	);

	// ---------------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------------

	// Obtain the dirty subtree for this section's questions array, if any.
	// dirtyFields shape mirrors the form values shape.
	const sectionDirtyFields =
		(
			dirtyFields as Record<string, unknown> & {
				surveyForm?: { surveyParts?: Array<{ questions?: unknown[] }> };
			}
		)?.surveyForm?.surveyParts?.[sectionIndex]?.questions ?? [];

	// Obtain the errors subtree for this section's questions array, if any.
	const sectionErrors =
		(
			errors as Record<string, unknown> & {
				surveyForm?: { surveyParts?: Array<{ questions?: unknown[] }> };
			}
		)?.surveyForm?.surveyParts?.[sectionIndex]?.questions ?? [];

	// Tag-prefix and custom-field mappings live in the ROOT `tagGenerationSettings`
	// / `customFieldMap` arrays (keyed by fieldId), NOT in the question's own RHF
	// subtree — so `hasDirtyLeaf` (which only inspects the question subtree) misses
	// them, and the per-question "unsaved" indicator never flips when a tag or
	// custom field is added/removed/changed (even though the form-level banner does
	// fire via shouldDirty). Mirror V1 — which calls `fieldStateManager.markFieldDirty`
	// on tag/custom-field edits — by treating a question as dirty when its mapping
	// differs from `formState.defaultValues`.
	const defaultTagSettings =
		(formState.defaultValues as { tagGenerationSettings?: Partial<ITagGenerationSettings> } | undefined)
			?.tagGenerationSettings?.customFormFieldTagSettings ?? [];
	const defaultCustomFieldMap =
		(formState.defaultValues as { customFieldMap?: ICustomFieldMap[] } | undefined)?.customFieldMap ?? [];

	const mappingDirtyForField = (fieldId?: string): boolean =>
		isQuestionMappingDirty({
			fieldId,
			currentTagSettings: tagGenerationSettings?.customFormFieldTagSettings,
			defaultTagSettings,
			currentCustomFieldMap: customFieldMap,
			defaultCustomFieldMap,
		});

	return (
		<>
			{watchedQuestions.map((field, questionIndex) => {
				const questionDirtyNode = (sectionDirtyFields as unknown[])[questionIndex];
				const questionErrorNode = (sectionErrors as unknown[])[questionIndex];

				const isDirty = hasDirtyLeaf(questionDirtyNode) || mappingDirtyForField(field.id);
				const hasBeenSaved = Boolean(field.id && savedQuestionIds.has(field.id));
				const hasError = hasErrorLeaf(questionErrorNode);
				const questionErrors = questionErrorNode as
					| { label?: { message?: string }; options?: { message?: string } }
					| undefined;

				// Only Dropdown/Checkbox questions surface tag-management actions in
				// the question dropdown — matches V1 `FieldList.tsx:151` gating.
				const isOptionField =
					field.fieldType === FormFieldType.MultipleOptions || field.fieldType === FormFieldType.Option;
				const tagManagerProps: TagManagerProps | undefined = isOptionField
					? {
							fieldId: field.id,
							fieldLabel: field.label,
							isOptionField: true,
							questionIndex,
							tagGenerationSettings,
							onTagPrefixChange,
							onTagPrefixDelete,
							disabled: false,
						}
					: undefined;

				// "Link Answer to Custom Field" is available on the same supported
				// types V1 allows in CustomSurvey (`AdditionalQuestions/index.tsx:148`).
				// Filter the current question's own mapping out of `usedCustomFieldIds`
				// so the user can re-pick their existing target on edit (matches V1
				// `FieldList.tsx:186`).
				const isSupportedCustomFieldType =
					field.fieldType === FormFieldType.String ||
					field.fieldType === FormFieldType.Option ||
					field.fieldType === FormFieldType.MultipleOptions ||
					field.fieldType === FormFieldType.FileUpload;
				const currentMapping = customFieldMap?.find(m => m.fieldId === field.id);
				const filteredUsedCustomFieldIds = usedCustomFieldIds.filter(id => id !== currentMapping?.contactFieldId);
				const customFieldManagerProps: CustomFieldManagerProps | undefined =
					hasCustomContactFields && isSupportedCustomFieldType
						? {
								fieldId: field.id,
								fieldLabel: field.label,
								isOptionField,
								customFieldMap,
								onCustomFieldChange,
								onCustomFieldDelete,
								disabled: false,
								availableCustomFields,
								usedCustomFieldIds: filteredUsedCustomFieldIds,
								questionIndex,
							}
						: undefined;

				return (
					<div key={field.id} className={css(questionStyleSheet.questionContainer)}>
						<QuestionField
							field={field}
							questionIndex={questionIndex}
							isEdit={isEdit}
							isDirty={isDirty}
							hasBeenSaved={hasBeenSaved}
							hasError={hasError}
							formFieldOptions={formFieldOptions}
							tagManagerProps={tagManagerProps}
							customFieldManagerProps={customFieldManagerProps}
							onFieldUpdate={onFieldUpdate}
							onFieldRemove={watchedQuestions.length > 1 ? onFieldRemove : undefined}
							onLabelChange={onLabelChange}
							onToggleOptional={onToggleOptional}
							onFieldTypeChange={onFieldTypeChange}
							questionErrors={questionErrors}
						>
							<QuestionVisibilityControls
								questionFieldNamePrefix={`${questionsPath}.${questionIndex}`}
								sectionIndex={sectionIndex}
								questionIndex={questionIndex}
								allSurveyParts={allSurveyParts}
							/>
						</QuestionField>
					</div>
				);
			})}
		</>
	);
};

SectionQuestionsBase.displayName = 'SectionQuestionsBase';

/**
 * Wrap with MobX `inject(ImpersonationContextKey)` so the component receives
 * `impersonationContext` — required by `useContactCustomFields` for admin
 * impersonation flows. Mirrors V1 `AdditionalQuestions` (`index.tsx:935`).
 */
export const SectionQuestions = inject(ImpersonationContextKey)(
	SectionQuestionsBase
) as React.FC<SectionQuestionsProps>;
